import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    HiOutlineCurrencyDollar,
    HiOutlineCalendar,
    HiOutlineChevronDown,
    HiOutlineChevronLeft,
    HiOutlineChevronRight,
    HiOutlineArrowLeft,
    HiOutlineRefresh,
    HiSearch,
} from "react-icons/hi";
import Card from "../components/shared/Card";
import Badge from "../components/shared/Badge";
import Button from "../components/shared/Button";
import TextInput from "../components/shared/TextInput";
import { getRevenueByMember } from "../redux/api/analyticsAPI";
import { dateRangeToBounds, formatRevenue } from "../utils/revenueAnalytics";
import {
    buildMemberGroupMap,
    formatMemberWithGroupsByIdentification,
} from "../utils/memberGroups";

const PAGE_SIZE = 25;
const DATE_RANGES = [
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
    "This Year",
    "All Time",
];

const RevenueByMember = () => {
    const organisationId = useSelector(
        (state) => state.organisation?.currentOrganisation?._id
    );
    const orgMembers = useSelector(
        (state) => state.organisation?.currentOrganisation?.members
    );
    const orgGroups = useSelector(
        (state) => state.organisation?.currentOrganisation?.groups
    );
    const memberGroupMap = useMemo(() => buildMemberGroupMap(orgGroups), [orgGroups]);

    const [dateRange, setDateRange] = useState("Last 30 Days");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const { startDate, endDate } = useMemo(
        () => dateRangeToBounds(dateRange),
        [dateRange]
    );

    const debounceRef = useRef();
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [dateRange]);

    useEffect(() => {
        if (!organisationId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getRevenueByMember({
            organisationId,
            startDate,
            endDate,
            search,
            page,
            pageSize: PAGE_SIZE,
        }).then((res) => {
            if (cancelled) return;
            if (res.error) {
                setError(res.error);
                setRows([]);
                setTotal(0);
            } else {
                setRows(res.data?.rows || []);
                setTotal(res.data?.total || 0);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [organisationId, startDate, endDate, search, page, reloadKey]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, total);

    return (
        <div className="analytics-container min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/analytics"
                            className="inline-flex items-center gap-1 text-body-md text-link hover:text-link-active"
                            data-help-title="Back to Analytics"
                            data-help="Returns to the main Analytics overview page."
                        >
                            <HiOutlineArrowLeft className="h-4 w-4" />
                            Analytics
                        </Link>
                        <span className="text-muted">/</span>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-surface-soft">
                                <HiOutlineCurrencyDollar className="h-6 w-6 text-ink" />
                            </div>
                            <h1 className="text-display-md text-ink" data-help-title="Revenue by Member" data-help="Shows revenue attributed to each individual member for the chosen date range, with their submissions, points earned, and revenue. You can search by name or identification.">Revenue by Member</h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            variant="icon-circular"
                            onClick={() => setReloadKey((k) => k + 1)}
                            aria-label="Refresh data"
                            data-help-title="Refresh data"
                            data-help="Reloads the per-member revenue figures using your current date range and search. The icon spins while data loads."
                        >
                            <HiOutlineRefresh
                                className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                            />
                        </Button>

                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowDatePicker((v) => !v)}
                                leadingIcon={<HiOutlineCalendar className="h-4 w-4" />}
                                trailingIcon={<HiOutlineChevronDown className="h-4 w-4" />}
                                data-help-title="Date range"
                                data-help="Opens the reporting period selector: Last 7 Days, Last 30 Days, Last 90 Days, This Year, or All Time. All figures on the page follow the chosen range."
                            >
                                {dateRange}
                            </Button>
                            <div
                                className={`absolute right-0 mt-2 w-48 rounded-md shadow-sm z-10 border border-hairline bg-canvas dropdown-menu ${
                                    showDatePicker ? "active" : ""
                                }`}
                            >
                                <div className="p-2">
                                    {DATE_RANGES.map((range) => (
                                        <button
                                            key={range}
                                            onClick={() => {
                                                setDateRange(range);
                                                setShowDatePicker(false);
                                            }}
                                            data-help-title="Date range option"
                                            data-help="Applies this reporting period and reloads the list from the first page."
                                            className={`block w-full text-left px-4 py-2 text-body-md rounded-sm ${
                                                dateRange === range
                                                    ? "bg-surface-soft text-ink"
                                                    : "text-body"
                                            }`}
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Card variant="default" className="!p-0 overflow-hidden">
                    <div className="p-5 border-b border-hairline flex items-center justify-between gap-4 flex-wrap">
                        <h2 className="text-title-md text-ink flex items-center gap-2">
                            <HiOutlineCurrencyDollar className="h-5 w-5 text-ink" />
                            <span>All members</span>
                        </h2>
                        <Badge variant="default" data-help-title="Member count" data-help="Shows how many members have submissions in the selected date range and search.">
                            {total.toLocaleString()} members
                        </Badge>
                    </div>

                    <div className="p-4 border-b border-hairline">
                        <TextInput
                            placeholder="Search by name or identification"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            leadingIcon={<HiSearch className="h-4 w-4" />}
                            inputClassName="w-full sm:w-96"
                            data-help-title="Search members"
                            data-help="Filters the list to members whose name or identification matches the typed text. Searching jumps back to the first page."
                        />
                    </div>

                    <div className="px-5 py-3 bg-surface-soft border-b border-hairline">
                        <div className="flex text-caption text-muted uppercase tracking-wide">
                            <div className="w-2/5 px-2">Member</div>
                            <div className="w-1/5 px-2 text-center">Submissions</div>
                            <div className="w-1/5 px-2 text-right">Points</div>
                            <div className="w-1/5 px-2 text-right">Revenue</div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="px-5 py-12 text-center text-body-md text-muted">
                            Loading…
                        </div>
                    ) : error ? (
                        <div className="px-5 py-12 text-center text-body-md text-signature-coral">
                            {error}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="px-5 py-12 text-center text-body-md text-muted">
                            No members with submissions in this range.
                        </div>
                    ) : (
                        <ul>
                            {rows.map((row) => (
                                <li
                                    key={row.identification}
                                    className="border-b border-hairline px-5 py-4"
                                    data-help-title="Member row"
                                    data-help="Shows a member's name with their group memberships, their identification, how many submissions they made in the range, total points earned, and revenue attributed to them."
                                >
                                    <div className="flex items-center">
                                        <div className="w-2/5 px-2">
                                            <div className="text-body-md font-medium text-ink">
                                                {formatMemberWithGroupsByIdentification(
                                                    row,
                                                    orgMembers,
                                                    memberGroupMap,
                                                    "No Name"
                                                )}
                                            </div>
                                            <div
                                                className="text-body-md mt-1 truncate max-w-[320px] text-muted"
                                                title={row.identification}
                                            >
                                                ID: {row.identification}
                                            </div>
                                        </div>
                                        <div className="w-1/5 px-2 text-center text-body-md text-body">
                                            {row.submissionsCount.toLocaleString()}
                                        </div>
                                        <div className="w-1/5 px-2 text-right text-body-md text-body">
                                            {row.totalPoints.toLocaleString()}
                                        </div>
                                        <div className="w-1/5 px-2 text-right text-body-md font-medium text-ink">
                                            {formatRevenue(row.totalRevenue)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {total > 0 && (
                        <div className="px-5 py-4 flex items-center justify-between bg-surface-soft">
                            <span className="text-body-md text-muted">
                                {rangeStart}–{rangeEnd} of {total.toLocaleString()}
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    leadingIcon={
                                        <HiOutlineChevronLeft className="h-4 w-4" />
                                    }
                                    data-help-title="Previous page"
                                    data-help="Shows the previous 25 members. Disabled on the first page."
                                >
                                    Prev
                                </Button>
                                <span className="text-body-md text-muted">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page >= totalPages || loading}
                                    onClick={() =>
                                        setPage((p) => Math.min(totalPages, p + 1))
                                    }
                                    trailingIcon={
                                        <HiOutlineChevronRight className="h-4 w-4" />
                                    }
                                    data-help-title="Next page"
                                    data-help="Shows the next 25 members. Disabled on the last page."
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default RevenueByMember;
